import DeleteIcon from "@mui/icons-material/Delete";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import type { GridPaginationModel, GridRenderCellParams } from "@mui/x-data-grid";
import { useSnackbar } from "notistack";
import React, { useCallback, useMemo, useState } from "react";
import { EmptyState } from "@/components/ui";
import { useApiMutation, useApiQuery } from "../../hooks";
import type { RunTestByIdParams, Test, TestRun } from "../../types";
import { DataGrid } from "../data-grid";
import { testColumns } from "../tests-table";
import { TestRunPanel } from "./test-run-panel";

interface PaginatedTestsResponse {
  items: Test[];
  total: number;
}

interface VibeTestActionColumnProps extends GridRenderCellParams<Test> {
  onRunTest?: (testId: string) => void;
  onViewRun?: (testId: string, testName: string) => void;
  onDeleteTest?: (testId: string) => void;
}

type RunProps = {
  test_ids: string[];
};

const VibeTestActionColumn = ({
  row,
  onRunTest,
  onViewRun,
  onDeleteTest,
}: VibeTestActionColumnProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { mutate: runTest, isPending: isRunningTest } = useApiMutation<RunProps, RunTestByIdParams>(
    "POST",
    `/vibe-qa/run-tests`,
    {
      onSuccess: () => {
        enqueueSnackbar("Test run started successfully!", {
          variant: "success",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
        onRunTest?.(row.id);
      },
    },
  );

  const { mutate: deleteTest, isPending: isDeletingTest } = useApiMutation(
    "DELETE",
    `/vibe-qa/tests/${row.id}`,
    {
      onSuccess: () => {
        enqueueSnackbar("Test deleted successfully", {
          variant: "success",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
        setDeleteDialogOpen(false);
        onDeleteTest?.(row.id);
      },
      onError: (error) => {
        enqueueSnackbar(`Error deleting test: ${error.message}`, {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
        setDeleteDialogOpen(false);
      },
    },
  );

  return (
    <>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Tooltip title="Run test">
          <IconButton
            size="small"
            color="default"
            disabled={isRunningTest || ["running"].includes(row.run_status ?? "pending")}
            onClick={() => {
              runTest({
                test_ids: [row.id],
              });
            }}
            aria-label="Run vibe test"
          >
            <PlayCircleIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="View latest run">
          <IconButton
            size="small"
            color="primary"
            onClick={() => onViewRun?.(row.id, row.name)}
            aria-label="View test run"
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete test">
          <IconButton
            size="small"
            color="error"
            onClick={() => setDeleteDialogOpen(true)}
            aria-label="Delete test"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Dialog open={deleteDialogOpen} onClose={() => !isDeletingTest && setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Test Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the test "{row.name}"? This action will also delete all
            related test runs.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeletingTest}>
            Cancel
          </Button>
          <Button onClick={() => deleteTest({})} color="error" disabled={isDeletingTest}>
            {isDeletingTest ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const RegressionTab = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [panelOpen, setPanelOpen] = useState(false);
  const [selectedTestId, setSelectedTestId] = useState<string | null>(null);
  const [selectedTestName, setSelectedTestName] = useState<string>("");
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  const queryUrl = useMemo(() => {
    const offset = paginationModel.page * paginationModel.pageSize;
    return `/vibe-qa/tests?limit=${paginationModel.pageSize}&offset=${offset}`;
  }, [paginationModel.page, paginationModel.pageSize]);

  const {
    data: testsResponse,
    isLoading: isLoadingTests,
    refetch: refetchTests,
  } = useApiQuery<PaginatedTestsResponse>(queryUrl, {
    refetchInterval: 5000,
  });

  const tests = testsResponse?.items ?? [];
  const totalTests = testsResponse?.total ?? 0;

  const handlePaginationModelChange = useCallback((newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
  }, []);

  const { mutate: runTest, isPending: isRunningTest } = useApiMutation<RunProps, RunTestByIdParams>(
    "POST",
    `/vibe-qa/run-tests`,
    {
      onSuccess: () => {
        enqueueSnackbar("Test runs started successfully!", {
          variant: "success",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
        refetchTests();
      },
    },
  );

  const { data: testRuns } = useApiQuery<TestRun[]>(
    `/vibe-qa/tests-runs?test_id=${selectedTestId}`,
    {
      enabled: !!selectedTestId && panelOpen,
      refetchInterval: panelOpen ? 3000 : false,
    },
  );

  const handleViewRun = (testId: string, testName: string) => {
    setSelectedTestId(testId);
    setSelectedTestName(testName);
    setPanelOpen(true);
  };

  const handleClosePanel = () => {
    setPanelOpen(false);
    setSelectedTestId(null);
    setSelectedRunId(null);
  };

  React.useEffect(() => {
    if (testRuns && testRuns.length > 0 && panelOpen) {
      setSelectedRunId(testRuns[0].id);
    }
  }, [testRuns, panelOpen]);

  return (
    <Box>
      <Card>
        <CardContent>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            pb={2}
          >
            <Stack direction="column" spacing={0} alignItems="flex-start">
              <Typography variant="h6">Regression tests</Typography>
              <Typography variant="caption" color="text.secondary">
                These are the tests that the QA sessions generated. Run them to ensure your
                application is working as expected after you make changes (regression testing).
              </Typography>
            </Stack>
            <Button
              variant="outlined"
              size="small"
              loading={isRunningTest}
              disabled={isRunningTest || totalTests === 0}
              onClick={() => {
                const testIds = tests.map((t) => t.id);
                if (testIds.length === 0) return;

                runTest({
                  test_ids: testIds,
                });
              }}
              sx={{ minWidth: 100 }}
            >
              Run all
            </Button>
          </Stack>

          {totalTests === 0 && !isLoadingTests && (
            <EmptyState message="No regression tests found. Start a QA session to create some!" />
          )}

          {(totalTests > 0 || isLoadingTests) && (
            <DataGrid
              loading={isLoadingTests}
              rows={tests}
              columns={[
                ...testColumns.filter((col) => ["name", "run_status"].includes(col.field)),
                {
                  field: "actions",
                  type: "actions",
                  headerName: "Actions",
                  width: 150,
                  renderCell: (params) => (
                    <VibeTestActionColumn
                      {...params}
                      onRunTest={() => refetchTests()}
                      onViewRun={handleViewRun}
                      onDeleteTest={() => refetchTests()}
                    />
                  ),
                  sortable: false,
                },
              ]}
              autoHeight
              paginationMode="server"
              rowCount={totalTests}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          )}
        </CardContent>
      </Card>

      <TestRunPanel
        runId={selectedRunId}
        testName={selectedTestName}
        open={panelOpen}
        onClose={handleClosePanel}
      />
    </Box>
  );
};
